import React from 'react';
import { Heart, ArrowLeft, Shield } from 'lucide-react';
import Header from './Header';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
   <Header/>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center mb-6">
            <Shield className="w-8 h-8 text-green-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          <p className="text-gray-600 mb-8">Last updated: June 11, 2025</p>

          <div className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Our Commitment to Privacy</h2>
              <p className="text-gray-700 mb-4">
                At VineMatched, we understand that privacy is crucial when seeking emotional support. This policy explains how we collect, use, and protect your information while maintaining the anonymous and safe environment you need.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
              
              <h3 className="text-lg font-medium text-gray-900 mb-3">Anonymous User Data</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>Anonymous user IDs (no personally identifiable information)</li>
                <li>General location (country/region only, for matching purposes)</li>
                <li>Chat timestamps and duration</li>
                <li>Platform usage statistics</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Technical Information</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>IP addresses (automatically deleted after 30 days)</li>
                <li>Device type and browser information</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Communication Content</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>Chat messages (encrypted and stored temporarily for quality assurance)</li>
                <li>Feedback and reports submitted by users</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>To match you with appropriate emotional support listeners</li>
                <li>To monitor conversations for safety and policy violations</li>
                <li>To improve our platform and user experience</li>
                <li>To generate anonymous analytics and insights</li>
                <li>To respond to user reports and safety concerns</li>
                <li>To comply with legal obligations when required</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Retention</h2>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li><strong>Chat messages:</strong> Automatically deleted after 7 days</li>
                <li><strong>User sessions:</strong> Cleared when you leave the platform</li>
                <li><strong>Safety reports:</strong> Retained for 30 days for investigation purposes</li>
                <li><strong>Analytics data:</strong> Aggregated and anonymized permanently</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">When We May Share Information</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-yellow-800">
                  We prioritize your privacy, but may share information in limited circumstances:
                </p>
              </div>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>When required by law or legal process</li>
                <li>To prevent imminent harm or danger to yourself or others</li>
                <li>To investigate violations of our Terms of Service</li>
                <li>With your explicit consent</li>
                <li>In case of a business transfer (users will be notified)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Security Measures</h2>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>End-to-end encryption for all conversations</li>
                <li>Secure servers with regular security audits</li>
                <li>Limited access to user data by authorized personnel only</li>
                <li>Regular deletion of temporary data</li>
                <li>Anonymous user identification system</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Rights</h2>
              <p className="text-gray-700 mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>Access information about your data usage</li>
                <li>Request deletion of your data (where technically feasible)</li>
                <li>Opt out of non-essential data collection</li>
                <li>Report privacy concerns or violations</li>
                <li>Leave the platform at any time</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                We do not knowingly collect personal information from children under 13. Users between 13-18 should have parental guidance. If we discover we have collected information from a child under 13, we will delete it immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this privacy policy periodically. Significant changes will be communicated via email or platform notification. Continued use of VineMatched after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                For privacy-related questions or concerns, contact us at:
                <br />
                <a href="mailto:connect@vinematched.com" className="text-red-600 hover:text-red-700">connect@vinematched.com</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

export default PrivacyPolicy;